import { Box, Flex, Grid } from 'components/box';
import { Heading, Text } from 'components/typography';
import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import {
  isConfigurable,
  isCustomizable,
  isPhysical,
} from 'types/guards/product';
import { CustomizableSelectOptionValue, ProductInterface } from 'types/types';
import { useCanShowProduct, useIsEarly, useIsTimedOut } from 'hooks/product';
import ProductCarousel from 'components/_shared/widgets/product/carousel';
import ProductPricing from './pricing';
import ProductActions from './actions';
import ProductMarkup from './markup';
import { GiftModal } from './modals';
import { ProductDetailsHorizontalProgressBar } from 'components/count-down-progress-bar';
import Pill from 'components/_shared/elements/pill';
import RelatedProducts from './related-products';
import { ClientOnly } from 'components/_shared/widgets/render-control';
import SharingActions from './sharing-actions';
import { css } from '@emotion/react';
import {
  isLimitedTimeDeal,
  catalogPageBreadcrumbs,
} from '../../../util/products';
import { useIsStaffMember } from 'hooks/customer';
import ProductNotActive from './not-active';
import {
  mapProductToPayload,
  trackProductDetailImpression,
  trackSizeGuideInteraction,
} from '../../../util/tag-manager';
import Button from 'components/button';
import { Link } from 'components/configurable-routing';
import { ChevronLeft as IconBack } from 'react-feather';
import RulerIcon from 'icons/ruler.svg';
import { SizeChartModal } from 'components/_pages/products/modals';
import InformationalButton from 'components/_pages/products/buttons/informational-button';
import { EventNamesEnum } from 'types/third-party/gtm';

const ParallelImportModal = dynamic(() => import('./modals/parallel-import'));

const MAX_DISPLAYED_PILLS = 2;

const Pills = ({ features }: { features?: string[] }) => {
  if (!features || features.length === 0) {
    return null;
  }

  return (
    <Flex
      className="hide-for-mobile-only"
      flexWrap="wrap"
      css={css`
        gap: 8px;
        white-space: nowrap;
      `}
    >
      {(features || []).map(feature => (
        <Pill key={feature}>{feature}</Pill>
      ))}
    </Flex>
  );
};

const generateMobileGridTemplate = (showSizeChart: boolean) =>
  [
    "'carousel'",
    ...(showSizeChart ? ["'size'"] : []),
    "'title'",
    "'actions'",
    "'additional'",
    "'price'",
    "'details'",
    "'button'",
  ].join(' ');

const generateDesktopGridTemplate = (showSizeChart: boolean) =>
  [
    "'carousel title'",
    "'carousel price'",
    ...(showSizeChart ? ["'carousel size'"] : []),
    "'carousel actions'",
    "'carousel button'",
    "'carousel additional'",
    "'carousel empty'",
    "'carousel pills'",
    "'details details'",
  ].join(' ');

const ProductDetails = ({ product }: { product: ProductInterface }) => {
  const isStaffMember = useIsStaffMember();

  const isEarly = useIsEarly(product.activeFromDate || '');
  const isTimedOut = useIsTimedOut(product.activeToDate || '');

  const canShowProduct = useCanShowProduct({
    isStaffMember,
    isEarly,
    isTimedOut,
    isClearanceSale: !!product.isClearanceSale,
  });

  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [isParallelImportModalOpen, setIsParallelImportModalOpen] = useState(
    false
  );
  const [isSizeChartOpen, setIsSizeChartOpen] = useState(false);

  const [selectedOption, setSelectedOption] = useState<
    CustomizableSelectOptionValue | undefined
  >(undefined);

  const breadcrumbs = catalogPageBreadcrumbs(product);

  const showProgressBar = isLimitedTimeDeal(product) && !isTimedOut && !isEarly;

  const hasExternalListingLink = !!product.externalListingLink;

  const showSizeChart = isPhysical(product) && !!product.sizeChart;

  const price = selectedOption?.price.fixed || product.price;
  const hasTags = product.blueTag || product.yellowTag || product.lockdownText;
  const activeTags = [product.blueTag, product.yellowTag, product.lockdownText]
    .filter(Boolean)
    .slice(0, MAX_DISPLAYED_PILLS);

  /**
   * GTM tracking.
   */
  useEffect(() => {
    trackProductDetailImpression(mapProductToPayload({ product }));
  }, [product]);

  return (
    <>
      <Box
        className="show-for-desktop"
        mb={3}
        pt={3}
        mt={-5}
        width="fit-content"
      >
        {typeof breadcrumbs !== 'undefined' && (
          <Flex
            pl={2}
            alignItems="center"
            css={theme => css`
              gap: 6px;
              padding-top: ${theme.space[2] + 7}px;
              padding-bottom: ${theme.space[2] + 7}px;
            `}
          >
            <Link {...breadcrumbs.pageLink} />
            {breadcrumbs.categoryLink && (
              <>
                <Text>/</Text>
                <Link {...breadcrumbs.categoryLink} />
              </>
            )}
          </Flex>
        )}
      </Box>

      <Grid
        gridAutoFlow={['row', null, 'unset']}
        gridColumnGap={[4, 5]}
        gridRowGap={[2, 3]}
        gridTemplateColumns={['1fr', null, '1fr 1fr']}
        gridTemplateAreas={[
          generateMobileGridTemplate(showSizeChart),
          null,
          generateDesktopGridTemplate(showSizeChart),
        ]}
        alignItems="start"
        mb={0}
      >
        <Box
          gridArea="carousel"
          width="100%"
          gridRow={['1/2', null, 'carousel/carousel']}
          m={['0 auto', null, 'initial']}
        >
          <ProductCarousel product={product} selectedOption={selectedOption} />
        </Box>

        <Box gridArea="title">
          {showProgressBar &&
            !!product.activeFromDate &&
            !!product.activeToDate && (
              <ClientOnly>
                <ProductDetailsHorizontalProgressBar
                  from={product.activeFromDate}
                  to={product.activeToDate}
                />
              </ClientOnly>
            )}

          <Grid
            gridTemplateColumns={['1fr auto', hasTags ? 'auto 1fr' : '1fr']}
            mt={[3, 0]}
          >
            <Heading as="h1">
              <Text
                as="span"
                fontSize={[2, 4]}
                mb="1"
                pb="0"
                display="flex"
                fontFamily="header"
              >
                {product.brand}
              </Text>

              <Text
                as="span"
                color="black"
                fontWeight="400"
                lineHeight="1.3"
                letterSpacing="0.1"
                fontSize={[1, 3, 2]}
                fontFamily="header"
              >
                {product.name}
              </Text>
            </Heading>

            {hasTags && (
              <Box className="hide-for-mobile-only">
                {activeTags.map(tag => (
                  <Flex
                    alignItems="flex-end"
                    flexDirection="column"
                    justifyContent="space-between"
                    mb={1}
                    key={tag}
                  >
                    <Pill
                      css={theme => css`
                        font-size: ${theme.fontSizes[0]};
                      `}
                      noWrap
                    >
                      {tag}
                    </Pill>
                  </Flex>
                ))}
              </Box>
            )}

            <Box className="show-for-mobile-only">
              <SharingActions product={product} />
            </Box>
          </Grid>

          {activeTags.map(tag => (
            <Box mt={1} className="hide-for-tablet" key={tag}>
              <Pill
                css={theme => css`
                  font-size: ${theme.fontSizes[0]};
                `}
                noWrap
              >
                {tag}
              </Pill>
            </Box>
          ))}
        </Box>

        {showSizeChart && (
          <Flex gridArea="size" width="100%" my="-10px">
            <InformationalButton
              onClick={() => {
                setIsSizeChartOpen(true);
                trackSizeGuideInteraction(EventNamesEnum.openSizeGuide);
              }}
              iconSrc={RulerIcon}
              text="View Size Info"
              highlighted
            />
          </Flex>
        )}

        <Box gridArea="price">
          <ProductPricing
            product={product}
            displayPrice={price}
            hidePriceRange={
              !!selectedOption &&
              selectedOption.price.fixed.value !== product.price.value
            }
            isTimedOut={product.isLunchtimeProduct && isTimedOut}
            canShowProduct={canShowProduct}
            openParallelImportModal={() => setIsParallelImportModalOpen(true)}
          />
        </Box>

        {(canShowProduct ||
          isCustomizable(product) ||
          isConfigurable(product)) && (
          <ProductActions
            product={product}
            openGiftModal={() => setIsGiftModalOpen(true)}
            displayPrice={price}
            canShowProduct={canShowProduct}
            setSelectedOption={setSelectedOption}
            isStaffMember={isStaffMember}
            hidePriceRange={
              !!selectedOption &&
              selectedOption.price.fixed.value !== product.price.value
            }
          />
        )}

        {!canShowProduct && (
          <ProductNotActive
            isTimedOut={isTimedOut}
            isEarly={isEarly}
            activeFromDate={product.activeFromDate}
          />
        )}

        <Grid
          gridArea="details"
          gridAutoFlow="row"
          gridColumnGap={[4, 5]}
          gridRowGap={4}
          gridTemplateColumns={['1fr', null, '1fr 1fr']}
          gridTemplateRows="repeat(1, min-content)"
          alignItems="start"
          pt={[0, 4]}
          mt={[hasExternalListingLink ? -40 : null, 0, 3]}
          mb={[2, null, 0]}
          css={theme => css`
            @media ${theme.mediaQueries.desktopUp} {
              border-top: 1px solid ${theme.colors.darkishGrey};
            }
          `}
        >
          {product.description && <ProductMarkup body={product.description} />}

          {product.writeup && (
            <ProductMarkup
              highlighted
              body={product.writeup.body}
              color={product.writeup.color}
            />
          )}
        </Grid>
      </Grid>

      <RelatedProducts />

      <GiftModal
        close={() => setIsGiftModalOpen(false)}
        isOpen={isGiftModalOpen}
      />

      {isPhysical(product) && product.isParallelImport && (
        <ParallelImportModal
          close={() => setIsParallelImportModalOpen(false)}
          isOpen={isParallelImportModalOpen}
        />
      )}

      {showSizeChart && !!product.sizeChart && (
        <SizeChartModal
          close={() => setIsSizeChartOpen(false)}
          isOpen={isSizeChartOpen}
          sizeChart={product.sizeChart}
          sizeChartImage={product.sizeChartImage}
        />
      )}
    </>
  );
};

export default ProductDetails;
